import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { WawiInvoiceDraftDto } from '../../../wa-wi/gemeinkosten/gemeinkosten-invoice-review/gemeinkosten-invoice.models';
import { Institution } from '../../institutionen/institution';
import { SonstigerDebitor } from '../../sonstige-debitoren/sonstiger-debitor';
import { SonstigerErloes, SonstigerErloesPreis } from '../sonstiger-erloes';

export interface SonstigerErloesInvoiceReviewDialogData {
    draft: WawiInvoiceDraftDto;
    pdfFile: File | null;
    erloes?: SonstigerErloes;
    mode?: 'review' | 'edit';
}

@Component({
    selector: 'kiene-sonstiger-erloes-invoice-review-dialog',
    templateUrl: './sonstiger-erloes-invoice-review-dialog.component.html',
    styleUrls: ['./sonstiger-erloes-invoice-review-dialog.component.scss'],
})
export class SonstigerErloesInvoiceReviewDialogComponent implements OnInit, OnDestroy {
    @ViewChild('pdfPane') pdfPane: ElementRef<HTMLDivElement>;

    partnerArt: 'INSTITUTION' | 'DEBITOR' = 'INSTITUTION';
    institution: Institution = null;
    debitor: SonstigerDebitor = null;
    belegnrCtrl = new FormControl('');
    datumCtrl = new FormControl(new Date(), Validators.required);
    leistVonCtrl = new FormControl(null);
    leistBisCtrl = new FormControl(null);
    kommentarCtrl = new FormControl('');
    freigegebenCtrl = new FormControl(false);
    preise: Array<SonstigerErloesPreis & { erloes?: any }> = [];
    mwstSaetze = [0, 7, 19];
    aktivParams = new HttpParams().set('aktiv', '1');
    erloesParams = new HttpParams().set('erloes_sonstig', '1').set('aktiv', '1');
    warnings: string[] = [];
    extractNetto: number | null = null;
    extractBrutto: number | null = null;
    pdfSrc: Uint8Array | null = null;
    pdfObjectUrl: string | null = null;
    page = 1;
    totalPages = 0;
    pdfLoading = false;
    saving = false;

    constructor(
        public dialogRef: MatDialogRef<SonstigerErloesInvoiceReviewDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SonstigerErloesInvoiceReviewDialogData,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneTableService,
        private readonly messageService: MessageService,
    ) {}

    ngOnInit(): void {
        const d = this.data?.draft;
        const e = this.data?.erloes;
        this.warnings = [...(d?.warnings ?? [])];
        const absender = (d?.suggestedLieferantName || d?.extract?.lieferantName || '').trim();
        if (absender) {
            this.warnings.unshift(`Erkannter Absender: ${absender}`);
        }

        this.belegnrCtrl.setValue(e?.belegnr || d?.suggestedRechnungNr || d?.extract?.rechnungsNr || '');
        this.kommentarCtrl.setValue(e?.kommentar || d?.kommentar || '');
        this.freigegebenCtrl.setValue((e?.freigegeben || 0) > 0);
        const rawDatum = e?.datum || d?.suggestedDatum;
        if (rawDatum) {
            this.datumCtrl.setValue(new Date(rawDatum));
        }
        if (e?.leistungsdatum_von) {
            this.leistVonCtrl.setValue(new Date(e.leistungsdatum_von));
        } else if (d?.suggestedLeistungsdatumVon) {
            this.leistVonCtrl.setValue(new Date(d.suggestedLeistungsdatumVon));
        }
        if (e?.leistungsdatum_bis) {
            this.leistBisCtrl.setValue(new Date(e.leistungsdatum_bis));
        } else if (d?.suggestedLeistungsdatumBis) {
            this.leistBisCtrl.setValue(new Date(d.suggestedLeistungsdatumBis));
        }

        if (e?.sonstiger_debitor_id) {
            this.partnerArt = 'DEBITOR';
            this.debitor = {
                sonstiger_debitor_id: e.sonstiger_debitor_id,
                bezeichnung: e.partner,
            };
        } else if (e?.institution_id) {
            this.partnerArt = 'INSTITUTION';
            this.institution = { institution_id: e.institution_id, bezeichnung: e.partner };
        }

        const parsed = this.parsePreise(e);
        if (parsed.length) {
            this.preise = parsed;
        } else {
            const fromDraft = (d?.preise || []).map((p: any, i: number) => ({
                position: i + 1,
                bezeichnung: p.bezeichnung || '',
                kosten_netto: Number(p.kostenNetto ?? p.kosten_netto) || 0,
                mwstsatz: this.normalizeMwst(p.mwstsatz),
                fibu_erloes_konto_id: null,
                erloes: null,
            }));
            this.preise = fromDraft.length
                ? fromDraft
                : [
                      {
                          position: 1,
                          bezeichnung: '',
                          kosten_netto: Number(d?.extract?.nettoGesamt) || 0,
                          mwstsatz: this.normalizeMwst(d?.extract?.mwstSatz),
                          fibu_erloes_konto_id: null,
                          erloes: null,
                      },
                  ];
        }

        this.extractNetto = d?.extract?.nettoGesamt != null ? Number(d.extract.nettoGesamt) : null;
        this.extractBrutto = d?.extract?.bruttoGesamt != null ? Number(d.extract.bruttoGesamt) : null;
        this.initPdfViewer();
        this.hydratePartnerKonto();
    }

    ngOnDestroy(): void {
        this.revokePdfUrl();
    }

    onPartnerArtChange() {
        if (this.partnerArt === 'INSTITUTION') {
            this.debitor = null;
        } else {
            this.institution = null;
        }
    }

    onInstitutionSelected(i: Institution) {
        this.institution = i;
        this.applyDefaultErloes(i);
    }

    onDebitorSelected(d: SonstigerDebitor) {
        this.debitor = d;
        this.applyDefaultErloes(d);
    }

    addPreis() {
        this.preise.push({
            position: this.preise.length + 1,
            bezeichnung: '',
            kosten_netto: 0,
            mwstsatz: 0,
            fibu_erloes_konto_id: null,
            erloes: this.defaultErloesObject(),
        });
    }

    removePreis(i: number) {
        if (this.preise.length < 2) {
            return;
        }
        this.preise.splice(i, 1);
    }

    isValid(): boolean {
        const hasPartner =
            this.partnerArt === 'INSTITUTION'
                ? !!this.institution?.institution_id
                : !!this.debitor?.sonstiger_debitor_id;
        const hasPreis = this.preise.some(p => Number(p.kosten_netto) > 0 && !!p.erloes?.fibu_konto_id);
        return this.datumCtrl.valid && hasPartner && hasPreis;
    }

    onPdfLoad(pdf: { numPages?: number }) {
        this.totalPages = pdf?.numPages ?? 0;
        this.pdfLoading = false;
    }

    onPdfError(_err: unknown) {
        this.pdfLoading = false;
        this.messageService.infoMessage('PDF-Anzeige fehlgeschlagen – „PDF öffnen“ nutzen.');
    }

    prevPage() {
        if (this.page > 1) {
            this.page -= 1;
        }
    }

    nextPage() {
        if (this.totalPages && this.page < this.totalPages) {
            this.page += 1;
        }
    }

    reloadPdf() {
        this.initPdfViewer();
    }

    openPdfInNewTab() {
        if (this.pdfObjectUrl) {
            window.open(this.pdfObjectUrl, '_blank');
        }
    }

    save() {
        if (!this.isValid()) {
            this.messageService.infoMessage('Partner, Betrag, Erlöskonto und Datum sind erforderlich.');
            return;
        }
        this.saving = true;
        const body: any = {
            sonstiger_erloes_id: this.data?.erloes?.sonstiger_erloes_id,
            belegnr: (this.belegnrCtrl.value || '').trim() || null,
            datum: this.toDateStr(this.datumCtrl.value),
            leistungsdatum_von: this.toDateStr(this.leistVonCtrl.value),
            leistungsdatum_bis: this.toDateStr(this.leistBisCtrl.value),
            kommentar: (this.kommentarCtrl.value || '').trim() || null,
            freigegeben: this.freigegebenCtrl.value ? 1 : 0,
            scan_review: 0,
            institution_id: this.partnerArt === 'INSTITUTION' ? this.institution?.institution_id : null,
            sonstiger_debitor_id: this.partnerArt === 'DEBITOR' ? this.debitor?.sonstiger_debitor_id : null,
            preise: this.preise
                .filter(p => Number(p.kosten_netto) > 0 && p.erloes?.fibu_konto_id)
                .map((p, i) => ({
                    position: i + 1,
                    bezeichnung: p.bezeichnung || null,
                    kosten_netto: Number(p.kosten_netto),
                    mwstsatz: this.normalizeMwst(p.mwstsatz),
                    fibu_erloes_konto_id: p.erloes.fibu_konto_id,
                })),
        };
        this.api.post(this.apiBaseUrl + 'sonstiger_erloes/update.php', body).subscribe({
            next: () => {
                this.saving = false;
                this.messageService.infoMessage('Sonstiger Erlös gespeichert.');
                this.dialogRef.close(true);
            },
            error: err => {
                this.saving = false;
                this.messageService.errorMessage('Speichern fehlgeschlagen.', err);
            },
        });
    }

    close() {
        this.dialogRef.close(false);
    }

    private hydratePartnerKonto() {
        if (this.partnerArt === 'INSTITUTION' && this.institution?.institution_id) {
            this.api
                .get(
                    this.apiBaseUrl + 'institution/read_one.php',
                    new HttpParams().set('institution_id', String(this.institution.institution_id)),
                )
                .subscribe({
                    next: (res: any) => {
                        const rec = res?.records?.[0] || res;
                        if (rec?.institution_id) {
                            this.institution = Object.assign({}, this.institution, rec);
                            this.applyDefaultErloes(this.institution);
                        }
                    },
                });
        } else if (this.partnerArt === 'DEBITOR' && this.debitor?.sonstiger_debitor_id) {
            this.api
                .get(
                    this.apiBaseUrl + 'sonstiger_debitor/read_one.php',
                    new HttpParams().set('sonstiger_debitor_id', String(this.debitor.sonstiger_debitor_id)),
                )
                .subscribe({
                    next: (res: any) => {
                        const rec = res?.records?.[0] || res;
                        if (rec?.sonstiger_debitor_id) {
                            this.debitor = Object.assign({}, this.debitor, rec);
                            this.applyDefaultErloes(this.debitor);
                        }
                    },
                });
        }
    }

    private applyDefaultErloes(partner: Institution | SonstigerDebitor) {
        const obj = this.toErloesObject(partner);
        if (!obj) {
            return;
        }
        for (const p of this.preise) {
            if (!p.erloes?.fibu_konto_id) {
                p.erloes = { ...obj };
                p.fibu_erloes_konto_id = obj.fibu_konto_id;
            }
        }
    }

    private defaultErloesObject() {
        return this.toErloesObject(this.partnerArt === 'INSTITUTION' ? this.institution : this.debitor);
    }

    private toErloesObject(partner: Institution | SonstigerDebitor) {
        if (!partner?.default_erloes_konto_id) {
            return null;
        }
        return {
            fibu_konto_id: partner.default_erloes_konto_id,
            konto_nr: partner.erloes_konto_nr,
            bezeichnung: partner.erloes_konto,
        };
    }

    private parsePreise(e?: SonstigerErloes): Array<SonstigerErloesPreis & { erloes?: any }> {
        let raw: any = e?.preise || e?.preise_as_json;
        if (typeof raw === 'string') {
            try {
                raw = JSON.parse(raw);
            } catch {
                raw = [];
            }
        }
        if (!Array.isArray(raw)) {
            return [];
        }
        return raw.map((p: SonstigerErloesPreis) => ({
            ...p,
            mwstsatz: this.normalizeMwst(p.mwstsatz),
            erloes: p.fibu_erloes_konto_id
                ? {
                      fibu_konto_id: p.fibu_erloes_konto_id,
                      konto_nr: p.erloes_konto_nr,
                      bezeichnung: p.erloes_konto,
                  }
                : null,
        }));
    }

    private normalizeMwst(v: any): number {
        const n = Number(v);
        return n === 7 || n === 19 ? n : 0;
    }

    private toDateStr(d: Date | string): string | null {
        if (!d) {
            return null;
        }
        const dt = d instanceof Date ? d : new Date(d);
        if (isNaN(dt.getTime())) {
            return null;
        }
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const day = String(dt.getDate()).padStart(2, '0');
        return `${dt.getFullYear()}-${m}-${day}`;
    }

    private initPdfViewer() {
        this.revokePdfUrl();
        const file = this.data?.pdfFile;
        if (!file) {
            this.pdfSrc = null;
            return;
        }
        this.pdfLoading = true;
        file.arrayBuffer()
            .then(buf => {
                this.pdfSrc = new Uint8Array(buf);
                this.pdfObjectUrl = URL.createObjectURL(file);
                this.pdfLoading = false;
            })
            .catch(() => {
                this.pdfLoading = false;
                this.pdfSrc = null;
            });
    }

    private revokePdfUrl() {
        if (this.pdfObjectUrl) {
            URL.revokeObjectURL(this.pdfObjectUrl);
            this.pdfObjectUrl = null;
        }
    }
}
